// Pure helpers ported from catalyst_tracker/static/tracker.js. Kept framework-free so they're easy to
// unit test and reuse between the list/calendar/filter components.
import { TrackerCatalyst, TrackerCatalystImpact, TrackerCompanyTier } from '../../types';

export const CATALYST_TYPES: string[] = [
  'Topline', 'Conference data', 'Regulatory decision', 'Filing',
  'Trial initiation', 'Enrollment complete', 'Other',
];

export const MODALITIES: string[] = [
  'Small molecule', 'mAb', 'Bispecific', 'ADC', 'Cell therapy', 'Radioligand', 'Degrader', 'Vaccine', 'Other',
];

export const OUTCOMES: Array<[string, string]> = [
  ['pending', 'Pending'], ['positive', 'Positive'], ['approved', 'Approved'],
  ['mixed', 'Mixed'], ['negative', 'Negative'], ['CRL', 'CRL'],
];
export const OUTCOME_LABEL: Record<string, string> = Object.fromEntries(OUTCOMES);

export const SIGNALS: string[] = ['Practice-changing', 'Proof of concept', 'New indication'];
export const COMPANY_TIERS: TrackerCompanyTier[] = ['Large/Mid cap', 'Small cap', 'Private'];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (n: number) => String(n).padStart(2, '0');

export function today(): string {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function addDays(iso: string, d: number): string {
  const t = new Date(iso + 'T12:00:00');
  t.setDate(t.getDate() + d);
  return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`;
}

export function fmtDay(iso?: string): string {
  if (!iso) return '—';
  const [, m, d] = iso.split('-');
  return `${MONTHS[+m - 1]} ${+d}`;
}

export function fmtLong(iso?: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${MONTHS[+m - 1]} ${+d}, ${y}`;
}

export function fmtMonthYear(iso: string): string {
  const [y, m] = iso.split('-');
  return `${MONTHS[+m - 1]} ${y}`;
}

export const tierOf = (r: TrackerCatalyst): TrackerCompanyTier => (r.tier as TrackerCompanyTier) || 'Large/Mid cap';
export const assetKey = (r: TrackerCatalyst) => `${(r.company || '').trim()}|${(r.asset || '').trim()}`.toLowerCase();

const HIST_MAP: Array<[RegExp, string]> = [
  [/^(solid tumou?rs?\b|pan-tumou?r|multiple$|basket)/i, 'Solid tumors (multiple)'],
  [/^breast/i, 'Breast'], [/^prostate( cancer)?$/i, 'Prostate'],
  [/urothelial|bladder/i, 'Bladder/Urothelial'], [/cholangio|biliary/i, 'Biliary'],
  [/glioma|glioblastoma|dipg|pontine|^cns\b/i, 'Glioma/CNS'], [/gastric|gej|esophag|gastroesoph/i, 'Gastroesophageal'],
  [/^head (and|&) neck/i, 'Head & Neck'],
  [/myelofibrosis|^mpn\b|myeloproliferative|polycythemia|thrombocyth/i, 'Myelofibrosis/MPN'],
  [/^(hcc|hepatocellular)/i, 'HCC'], [/^(rcc|renal)/i, 'RCC'], [/^al amyloid/i, 'Other heme'],
  [/^cll(\/sll)?$/i, 'CLL'], [/^desmoid tumou?rs?$/i, 'Desmoid tumor'],
  [/^neuroendocrine($| carcinoma$| tumou?r$)/i, 'Neuroendocrine'],
  [/^ovarian($| cancer$)/i, 'Ovarian'], [/^pancreatic($| cancer$| ductal adenocarcinoma$)/i, 'Pancreatic'],
  [/^(soft tissue )?sarcoma$/i, 'Sarcoma'],
];
const HIST_ALIAS: Record<string, string> = { crc: 'Colorectal', mcrc: 'Colorectal', 'colorectal cancer': 'Colorectal' };

export function normHist(h?: string): string {
  h = (h || '').trim();
  const alias = HIST_ALIAS[h.toLowerCase()];
  if (alias) return alias;
  if (/^uveal/i.test(h)) return 'Uveal melanoma';
  for (const [re, v] of HIST_MAP) if (re.test(h)) return v;
  return h;
}

/** Guidance-window string ("Q4 2026", "2H 2026", "PDUFA 2026-11-20"...) formatted for compact display. */
export function shortWindow(r: TrackerCatalyst): string {
  const w = String(r.window || '');
  let m = w.match(/\b(Q[1-4]|[12]H|H[12])\s*'?(?:CY)?\s*(20\d\d)/i);
  if (m) {
    const a = m[1].toUpperCase();
    return `${/^H/.test(a) ? a[1] + 'H' : a} ${m[2]}`;
  }
  m = w.match(/\b(YE|year[- ]end|end of|mid|early|late)[- ]?\s*(20\d\d)/i);
  if (m) {
    const k = m[1].toLowerCase();
    return `${k === 'ye' || k.startsWith('year') || k === 'end of' ? 'YE' : k[0].toUpperCase() + k.slice(1)} ${m[2]}`;
  }
  m = w.match(/\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\.?\s+(20\d\d)\b/i);
  if (m) return `${m[1][0].toUpperCase()}${m[1].slice(1, 3).toLowerCase()} ${m[2]}`;
  if (/^\s*20\d\d\s*$/.test(w)) return w.trim();
  if (r.date) return `~${fmtDay(r.date)}`;
  return w.split(/[;(,]/)[0].trim().slice(0, 18) || 'TBD';
}

/** End of a guided window, used to tell whether an "upcoming" catalyst has quietly gone overdue. */
export function windowEnd(r: TrackerCatalyst): string | undefined {
  const w = r.window || '';
  let m = w.match(/\b([12])H\s*(20\d\d)\b/i);
  if (m) return m[1] === '1' ? `${m[2]}-06-30` : `${m[2]}-12-31`;
  m = w.match(/\bQ([1-4])\s*(20\d\d)\b/i);
  if (m) return `${m[2]}-${['03-31', '06-30', '09-30', '12-31'][+m[1] - 1]}`;
  m = w.match(/\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\.?\s+(20\d\d)\b/i);
  if (m) {
    const mi = 'janfebmaraprmayjunjulaugsepoctnovdec'.indexOf(m[1].slice(0, 3).toLowerCase()) / 3;
    return new Date(Date.UTC(+m[2], mi + 1, 0)).toISOString().slice(0, 10);
  }
  m = w.match(/^(?:FY\s*)?(20\d\d)\b(?!-\d)/i);
  if (m) return `${m[1]}-12-31`;
  return r.date;
}

export const isConf = (r: TrackerCatalyst) => !!(r.date && r.venue && /conference/i.test(r.type || ''));
export const isExact = (r: TrackerCatalyst) =>
  !!(r.date && (/^\d{4}-\d{2}-\d{2}$/.test(r.window || '') || /PDUFA/i.test(r.window || '') || r.status === 'past'));
const registryStarted = (r: TrackerCatalyst) =>
  /initiation|first patient|enrollment start|study start/i.test(`${r.type || ''} ${r.event || ''}`) &&
  /^(RECRUITING|ACTIVE_NOT_RECRUITING|ENROLLING_BY_INVITATION|COMPLETED)$/i.test(String(r.registry_status || ''));

export function evPast(r: TrackerCatalyst): boolean {
  const TODAY = today();
  if (r.status === 'past') return true;
  return !!(r.date && r.date < TODAY && (isConf(r) || isExact(r) || /^\d{4}-\d{2}-\d{2}\b/.test(r.window || '') || registryStarted(r)));
}

export function overdue(r: TrackerCatalyst): boolean {
  if (r.status !== 'upcoming' || !r.date) return false;
  const end = windowEnd(r) || r.date;
  return end < today() && !evPast(r);
}

export const flagged = (r: TrackerCatalyst) => !!r.delayed || overdue(r);

// Brand -> generic alias table so searching "Keytruda" finds rows stored under "pembrolizumab" and vice versa.
export const BRAND_ALIAS: Record<string, string> = {
  keytruda: 'pembrolizumab', padcev: 'enfortumab vedotin', amtagvi: 'lifileucel', avmapki: 'avutometinib',
  fakzynja: 'defactinib', aucatzyl: 'obecabtagene autoleucel', beqalzi: 'sonrotoclax', brukinsa: 'zanubrutinib',
  blenrep: 'belantamab mafodotin', blincyto: 'blinatumomab', calquence: 'acalabrutinib',
  carvykti: 'ciltacabtagene autoleucel', datroway: 'datopotamab deruxtecan', imfinzi: 'durvalumab',
  imjudo: 'tremelimumab', decnupaz: 'pivekimab sunirine', elrexfio: 'elranatamab',
  enhertu: 'trastuzumab deruxtecan', epkinly: 'epcoritamab', tepkinly: 'epcoritamab', erleada: 'apalutamide',
  etcamah: 'camizestrant', libtayo: 'cemiplimab', revtorpyk: 'gedatolisib',
  inlexzo: 'tar-200 (gemcitabine intravesical system)', inluriyo: 'imlunestrant', verzenio: 'abemaciclib',
  itovebi: 'inavolisib', jakafi: 'ruxolitinib', jaypirca: 'pirtobrutinib', jemperli: 'dostarlimab',
  jideytro: 'zidesamtinib', kimmtrak: 'tebentafusp', kisqali: 'ribociclib', komzifti: 'ziftomenib',
  lunsumio: 'mosunetuzumab', lynozyfic: 'linvoseltamab', modeyso: 'dordaviprone', monjuvi: 'tafasitamab',
  minjuvi: 'tafasitamab', niktimvo: 'axatilimab', nubeqa: 'darolutamide', orpathys: 'savolitinib',
  tagrisso: 'osimertinib', pluvicto: 'lutetium lu 177 vipivotide tetraxetan', rasonque: 'daraxonrasib',
  retevmo: 'selpercatinib', revuforj: 'revumenib', rybrevant: 'amivantamab', lazcluze: 'lazertinib',
  rytelo: 'imetelstat', sarclisa: 'isatuximab', scemblix: 'asciminib', talzenna: 'talazoparib',
  xtandi: 'enzalutamide', tecentriq: 'atezolizumab', tecvayli: 'teclistamab', talvey: 'talquetamab',
  darzalex: 'daratumumab', tevimbra: 'tislelizumab', trodelvy: 'sacituzumab govitecan', tukysa: 'tucatinib',
  veppanu: 'vepdegestrant', vyloy: 'zolbetuximab', welireg: 'belzutifan', lenvima: 'lenvatinib',
  xpovio: 'selinexor', zenbexus: 'iberdomide', zepzelca: 'lurbinectedin', ziihera: 'zanidatamab',
  zynlonta: 'loncastuximab tesirine', columvi: 'glofitamab', opdivo: 'nivolumab',
  opdualag: 'nivolumab + relatlimab', yervoy: 'ipilimumab', breyanzi: 'lisocabtagene maraleucel',
  abecma: 'idecabtagene vicleucel', imdelltra: 'tarlatamab', lumakras: 'sotorasib', krazati: 'adagrasib',
  tepmetko: 'tepotinib', kymriah: 'tisagenlecleucel', yescarta: 'axicabtagene ciloleucel',
  tecartus: 'brexucabtagene autoleucel', imbruvica: 'ibrutinib', venclexta: 'venetoclax',
  venclyxto: 'venetoclax', lynparza: 'olaparib', truqap: 'capivasertib', rezlidhia: 'olutasidenib',
  vanflyta: 'quizartinib', xospata: 'gilteritinib', ojjaara: 'momelotinib', inrebic: 'fedratinib',
  vonjo: 'pacritinib', augtyro: 'repotrectinib', ibtrozi: 'taletrectinib', gavreto: 'pralsetinib',
  polivy: 'polatuzumab vedotin', adcetris: 'brentuximab vedotin', kadcyla: 'trastuzumab emtansine',
  herceptin: 'trastuzumab', perjeta: 'pertuzumab', phesgo: 'pertuzumab/trastuzumab sc',
  ibrance: 'palbociclib', orserdu: 'elacestrant', elahere: 'mirvetuximab soravtansine',
  tivdak: 'tisotumab vedotin', lorbrena: 'lorlatinib', alecensa: 'alectinib', tafinlar: 'dabrafenib',
  mekinist: 'trametinib', braftovi: 'encorafenib', erbitux: 'cetuximab', vectibix: 'panitumumab',
  lonsurf: 'trifluridine/tipiracil', fruzaqla: 'fruquintinib', cabometyx: 'cabozantinib', inlyta: 'axitinib',
  rubraca: 'rucaparib', zejula: 'niraparib', akeega: 'niraparib/abiraterone', zytiga: 'abiraterone',
  orgovyx: 'relugolix', kyprolis: 'carfilzomib', revlimid: 'lenalidomide', pomalyst: 'pomalidomide',
  velcade: 'bortezomib', ninlaro: 'ixazomib', besponsa: 'inotuzumab ozogamicin',
  mylotarg: 'gemtuzumab ozogamicin', vidaza: 'azacitidine', onureg: 'oral azacitidine',
  inqovi: 'decitabine/cedazuridine', tibsovo: 'ivosidenib', idhifa: 'enasidenib', rydapt: 'midostaurin',
  bosulif: 'bosutinib', iclusig: 'ponatinib', sprycel: 'dasatinib', tasigna: 'nilotinib',
  gleevec: 'imatinib', qinlock: 'ripretinib', ayvakit: 'avapritinib', koselugo: 'selumetinib',
  ogsiveo: 'nirogacestat', lytgobi: 'futibatinib', pemazyre: 'pemigatinib', balversa: 'erdafitinib',
  truseltiq: 'infigratinib', tazverik: 'tazemetostat', danyelza: 'naxitamab', hepzato: 'melphalan/hds',
  elzonris: 'tagraxofusp', lumoxiti: 'moxetumomab', gazyva: 'obinutuzumab', rituxan: 'rituximab',
  arzerra: 'ofatumumab', zydelig: 'idelalisib', copiktra: 'duvelisib', aliqopa: 'copanlisib',
  ukoniq: 'umbralisib', tabrecta: 'capmatinib', exkivity: 'mobocertinib', gilotrif: 'afatinib',
  vizimpro: 'dacomitinib', iressa: 'gefitinib', tarceva: 'erlotinib', portrazza: 'necitumumab',
  cyramza: 'ramucirumab', avastin: 'bevacizumab', zaltrap: 'ziv-aflibercept', stivarga: 'regorafenib',
  nexavar: 'sorafenib', sutent: 'sunitinib', votrient: 'pazopanib', fotivda: 'tivozanib',
  bavencio: 'avelumab', zynyz: 'retifanlimab', loqtorzi: 'toripalimab', unloxcyt: 'cosibelimab',
  anktiva: 'nogapendekin alfa inbakicept', adstiladrin: 'nadofaragene firadenovec',
  imlygic: 'talimogene laherparepvec', tecelra: 'afamitresgene autoleucel', ryzneuta: 'efbemalenograstim',
  bizengri: 'zenocutuzumab', ensacove: 'ensartinib', voranigo: 'vorasidenib', gomekli: 'mirdametinib',
  romvimza: 'vimseltinib', emrelis: 'telisotuzumab vedotin', zegfrovy: 'sunvozertinib',
  hernexeos: 'zongertinib', ojemda: 'tovorafenib', imdylltra: 'tarlatamab',
};

const STRIP_PUNCT = /[-‐–—/·.,;:()]/g;

export function matchQuery(r: TrackerCatalyst, q: string): boolean {
  const h = [
    r.company, ([] as string[]).concat(r.partners || []).join(' '), r.ticker, r.asset, r.generic, r.target, r.moa,
    r.modality, r.indication, r.histology, r.phase, r.trial, r.nct, r.registry_title, r.event, r.venue,
    r.found_via, r.presentation, r.abstract_id, r.session_name, r.presenter, r.discussant, r.data, r.safety,
    r.population, r.signal, r.data_kind,
  ].filter(Boolean).join(' ').toLowerCase();
  const hay = h + ' ' + h.replace(STRIP_PUNCT, '');
  return q.toLowerCase().split(/\s+/).filter(Boolean).every((w) =>
    hay.includes(w) || hay.includes(w.replace(STRIP_PUNCT, '')) || (BRAND_ALIAS[w] && hay.includes(BRAND_ALIAS[w])));
}

export interface TrackerFilterState {
  when: 'upcoming' | 'past' | 'all';
  company: string;
  histology: string;
  area: string;
  tier: string;
  type: string;
  signal: string;
  q: string;
  delayedOnly: boolean;
  hideDelayed: boolean;
  highOnly: boolean;
  watchOnly: boolean;
  meeting: string;
  within: string;
}

export const DEFAULT_FILTERS: TrackerFilterState = {
  when: 'upcoming', company: '', histology: '', area: '', tier: '', type: '', signal: '', q: '',
  delayedOnly: false, hideDelayed: false, highOnly: false, watchOnly: false, meeting: '', within: '',
};

/** tracker.js's `data-when` click handler: Upcoming and Past are two independent toggles ("all" = both
 * on), but clicking the only active one keeps it on instead of leaving nothing shown. */
export function toggleWhen(current: TrackerFilterState['when'], side: 'upcoming' | 'past'): TrackerFilterState['when'] {
  const up = current === 'all' || current === 'upcoming';
  const pa = current === 'all' || current === 'past';
  let u2 = side === 'upcoming' ? !up : up;
  let p2 = side === 'past' ? !pa : pa;
  if (!u2 && !p2) { if (side === 'upcoming') u2 = true; else p2 = true; }
  return u2 && p2 ? 'all' : u2 ? 'upcoming' : 'past';
}

// ---------- saved views (tracker.js's VIEW_KEYS / #fView "Save current view…") ----------

export const VIEW_KEYS: (keyof TrackerFilterState)[] = [
  'when', 'company', 'histology', 'area', 'tier', 'type', 'signal', 'q',
  'delayedOnly', 'hideDelayed', 'highOnly', 'watchOnly', 'meeting', 'within',
];

export function snapshotView(filters: TrackerFilterState, sort: TrackerSort): Record<string, unknown> {
  const s: Record<string, unknown> = { sort };
  for (const k of VIEW_KEYS) s[k] = filters[k];
  return s;
}

export function applyView(s: Record<string, unknown>): { filters: Partial<TrackerFilterState>; sort?: TrackerSort } {
  const filters: Partial<TrackerFilterState> = {};
  for (const k of VIEW_KEYS) if (k in s) (filters as Record<string, unknown>)[k] = s[k];
  return { filters, sort: typeof s.sort === 'string' ? (s.sort as TrackerSort) : undefined };
}

export function inMeeting(r: TrackerCatalyst, conference: { name?: string; venue_match?: string[] } | undefined): boolean {
  if (!conference) return true;
  if (!/conference/i.test(r.type || '')) return false;
  const v = (r.venue || '').toLowerCase();
  const matches = (conference.venue_match && conference.venue_match.length ? conference.venue_match : [conference.name]);
  return matches.some((m) => m && v.includes(String(m).toLowerCase()));
}

export function baseFilter(r: TrackerCatalyst, s: TrackerFilterState, conference: { name?: string; venue_match?: string[] } | undefined, isWatched: (id: string) => boolean): boolean {
  if (s.company && r.company !== s.company) return false;
  if (s.histology && normHist(r.histology) !== s.histology
    && !(s.histology === 'Solid tumors (multiple)' && r.area === 'Solid')
    && !(s.histology === 'Heme (multiple)' && r.area === 'Heme')) return false;
  if (s.area && r.area !== s.area) return false;
  if (s.tier && tierOf(r) !== s.tier) return false;
  if (s.type && r.type !== s.type) return false;
  if (s.signal && r.signal !== s.signal) return false;
  if (s.highOnly && r.impact !== 'High') return false;
  if (s.delayedOnly && !flagged(r)) return false;
  if (s.hideDelayed && flagged(r)) return false;
  if (s.watchOnly && !isWatched(r.id)) return false;
  if (s.meeting && !inMeeting(r, conference)) return false;
  if (s.q && !matchQuery(r, s.q)) return false;
  return true;
}

export function visibleCatalysts(
  rows: TrackerCatalyst[], s: TrackerFilterState,
  conference: { name?: string; venue_match?: string[] } | undefined,
  isWatched: (id: string) => boolean,
): TrackerCatalyst[] {
  const TODAY = today();
  return rows.filter((r) => {
    if (!baseFilter(r, s, conference, isWatched)) return false;
    if (s.when !== 'all' && (s.when === 'past' ? !evPast(r) : evPast(r))) return false;
    if (s.within && !(r.date && r.date >= TODAY && r.date <= addDays(TODAY, +s.within))) return false;
    return true;
  });
}

const IMPACT_WEIGHT: Record<string, number> = { High: 3, Medium: 2, Low: 1 };

export type TrackerSort = 'date' | 'impact';

export function sortCatalysts(list: TrackerCatalyst[], sort: TrackerSort, when: TrackerFilterState['when']): TrackerCatalyst[] {
  if (sort === 'impact') {
    return list.slice().sort((a, b) =>
      (IMPACT_WEIGHT[b.impact as TrackerCatalystImpact] || 0) - (IMPACT_WEIGHT[a.impact as TrackerCatalystImpact] || 0)
      || (a.date || '9999').localeCompare(b.date || '9999'));
  }
  return list.slice().sort((a, b) => {
    const da = a.date || '9999', db = b.date || '9999';
    if (when === 'past') return db.localeCompare(da);
    return da.localeCompare(db) || (a.company || '').localeCompare(b.company || '');
  });
}

export function groupByMonth(list: TrackerCatalyst[]): Array<{ key: string; label: string; rows: TrackerCatalyst[] }> {
  const groups = new Map<string, TrackerCatalyst[]>();
  const noDate: TrackerCatalyst[] = [];
  for (const r of list) {
    if (!r.date) { noDate.push(r); continue; }
    const key = r.date.slice(0, 7);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(r);
  }
  const out = [...groups.entries()].map(([key, rows]) => ({ key, label: fmtMonthYear(key + '-01'), rows }));
  if (noDate.length) out.push({ key: 'tbd', label: 'Date TBD', rows: noDate });
  return out;
}

export const OUTCOME_BADGE_CLASSES: Record<string, string> = {
  pending: 'bg-slate-100 text-slate-600',
  positive: 'bg-emerald-100 text-emerald-700',
  approved: 'bg-emerald-100 text-emerald-700',
  mixed: 'bg-amber-100 text-amber-700',
  negative: 'bg-rose-100 text-rose-700',
  CRL: 'bg-rose-100 text-rose-700',
};

export const TYPE_BADGE_CLASSES: Record<string, string> = {
  Topline: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Conference data': 'bg-violet-50 text-violet-700 border-violet-200',
  'Regulatory decision': 'bg-blue-50 text-blue-700 border-blue-200',
  Filing: 'bg-amber-50 text-amber-700 border-amber-200',
  'Trial initiation': 'bg-sky-50 text-sky-700 border-sky-200',
  'Enrollment complete': 'bg-sky-50 text-sky-700 border-sky-200',
  Other: 'bg-slate-50 text-slate-600 border-slate-200',
};

export function studyDesc(r: TrackerCatalyst): string {
  return [r.phase, r.indication].filter(Boolean).join(' · ');
}

export function host(u?: string): string {
  if (!u) return 'Source';
  try { return new URL(u).hostname.replace(/^www\./, ''); } catch { return 'Source'; }
}

// ---- ported 1:1 from tracker.js for the restyled (tracker.css-driven) markup ----

/** `.kind` badge class: the catalyst type's first word (tracker.css colors `.kind.Topline/.Conference/...`). */
export function kindClass(type?: string): string {
  return (type || 'Other').split(' ')[0];
}

export const shortVenue = (v?: string) => String(v || '').replace(/\s*\([^)]*\)/g, '').replace(/^EORTC-NCI-AACR\b/, 'ENA').trim();

export function confDayKnown(r: TrackerCatalyst): boolean {
  if (!r.date) return false;
  if (r.session_start || r.status === 'past' || r.window === r.date || (r.window || '').includes(r.date)) return true;
  const MONTHS_RE = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const re = new RegExp(`\\b${MONTHS_RE[+r.date.slice(5, 7) - 1]}\\s+${+r.date.slice(8)}\\b(?!\\s*[-–]\\s*\\d)`);
  return re.test(r.window || '');
}

export interface WhenCell { cls: string; title?: string; d: string; y: string; }

/** The `.when` date cell shown at the left of a catalyst row — mirrors tracker.js's whenCell0/whenCell. */
export function whenCell(r: TrackerCatalyst): WhenCell {
  if (!r.date) return { cls: 'when', d: 'TBD', y: r.window || 'Not guided' };
  if (isConf(r)) {
    const known = confDayKnown(r);
    return {
      cls: known ? 'when' : 'when tip-l',
      title: known ? undefined : `Day estimated within ${r.venue}`,
      d: fmtDay(r.date),
      y: `${shortVenue(r.venue)}${known ? '' : ' · est.'}`,
    };
  }
  if (isExact(r)) return { cls: 'when', d: fmtDay(r.date), y: r.date.slice(0, 4) };
  return { cls: 'when tip-l', title: r.window, d: shortWindow(r), y: `est.${r.window ? ` · ${r.window}` : ''}` };
}

/** `.data .k` label above a reported result / guidance blurb. */
export function presLabel(r: TrackerCatalyst): string {
  if (r.data_kind === 'presented') return `Presented${r.presented_at ? ` · ${fmtLong(String(r.presented_at).slice(0, 10))}` : ''}`;
  if (r.data_kind === 'abstract') return r.date && r.date < today() ? 'Abstract · presentation not yet confirmed' : 'Abstract';
  return r.status === 'past' ? 'Data' : 'Guidance';
}

/** `.data` background-tint modifier: green unless the result was negative/CRL. */
export function presCls(r: TrackerCatalyst): string {
  return r.data && (r.data_kind === 'presented' || (r.status === 'past' && /^presented/i.test(r.data || ''))) && !['negative', 'CRL'].includes(r.outcome || '') ? ' pres' : '';
}

export const favPassed = (r: TrackerCatalyst) => r.status === 'past' || (!!r.date && r.date < today());
export const favRead = (r: TrackerCatalyst, read: Record<string, string> | undefined) => {
  const t = (read || {})[r.id];
  return !!t && (!r.updatedAt || r.updatedAt <= t);
};
export const favUnread = (r: TrackerCatalyst, read: Record<string, string> | undefined, isWatched: boolean) =>
  isWatched && favPassed(r) && !favRead(r, read);
